import { forwardRef, type Ref, useState } from 'react';

import Checkmark from '@crehana/icons/outline/Checkmark';
import Copy from '@crehana/icons/outline/Copy';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, TextField } from '@crehana/ui';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import type { CopyTextProps } from './types';

const SlugCopyAdornment = ({
  checkVisible,
  onClick,
  isDark = false,
}: {
  checkVisible: boolean;
  onClick: () => void;
  isDark?: boolean;
}) => {
  const { primaryLight, secondaryMain, greenMain } = colors;

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (checkVisible) return <Checkmark color={greenMain} size={24} />;
  return (
    // TODO: talk with the code owner to fix this code so we can enable this rule again
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div onClick={onClick}>
      <Copy
        className="cursor-pointer"
        color={isDark ? secondaryMain : primaryLight}
        size={24}
      />
    </div>
  );
};

const CopyText = (
  { darkMode, value, msgTextCopied, ...otherProps }: CopyTextProps,
  ref: Ref<HTMLInputElement>,
) => {
  const { openSnackbar } = useAdminDashboard();
  const [checkVisible, setCheckVisible] = useState(false);

  const showTimedCheck = () => {
    setCheckVisible(true);
    const checkVisibleTime = setTimeout(() => setCheckVisible(false), 5000);
    return () => clearTimeout(checkVisibleTime);
  };

  const handleCopyToClipboard = async () => {
    showTimedCheck();
    await navigator.clipboard.writeText(value?.toString() || '');
    openSnackbar({
      type: 'success',
      msg: msgTextCopied || '',
    });
  };
  return (
    <TextField
      {...otherProps}
      ref={ref}
      value={value}
      darkMode={darkMode}
      inputProps={{ readOnly: true }}
      endAdornment={
        <SlugCopyAdornment
          checkVisible={checkVisible}
          onClick={handleCopyToClipboard}
          isDark={darkMode}
        />
      }
    />
  );
};

export default forwardRef(CopyText);
